'use client';

import clsx from 'clsx';
import NextImage from 'next/image';
import Link from 'next/link';
import { Suspense, useState } from 'react';

import { Hamburger } from '@/components/core/Hamburger/Hamburger';
import { useLanguageParam, useScrollDirection } from '@/hooks';
import { useOutsideClick } from '@/hooks/useClickOutside';
import { AlternateStoryblokStory, ScrollDirection } from '@/types/common';
import { HeaderMenuStoryblok, MicrocopyStoryblok } from '@/types/storyblok/storyblok-types';

import { CurrencySwitcher } from './components/CurrencySwitcher/CurrencySwitcher';
import { LanguageSwitcher } from './components/LanguageSwitcher/LanguageSwitcher';
import { SubmenuDesktop } from './components/Submenu/desktop/SubmenuDesktop';
import { SubmenuMobile } from './components/Submenu/mobile/SubmenuMobile';

import styles from './Header.module.scss';

type HeaderProps = {
    blok: HeaderMenuStoryblok;
    translations: MicrocopyStoryblok;
    alternates?: AlternateStoryblokStory[];
    buttonComponent?: React.ReactNode;
};

export const Header: React.FC<HeaderProps> = ({ blok, translations, alternates, buttonComponent }) => {
    const language = useLanguageParam();
    const scrollDir = useScrollDirection();
    const [hovered, setHovered] = useState<boolean>(false);

    const [menuIds, setMenuIds] = useState<string[]>(['intial']);
    const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);

    const toggleMenuVisibility = (menuId: string, level: number) => {
        setMenuIds(prevMenus => {
            if (level >= prevMenus.length) {
                const newMenus = prevMenus.slice(0, level);
                newMenus[level] = menuId;
                return newMenus;
            }
            if (level === 1) {
                return prevMenus[1] === menuId ? ['initial'] : ['initial', menuId];
            }
            return prevMenus.slice(0, level);
        });
    };

    const ref = useOutsideClick(() => setMenuIds(['initial']));
    return (
        <div
            ref={ref}
            className={clsx(styles.root, {
                [styles.menuOpen]: showMobileMenu,
                [styles.scrollDown]: scrollDir === ScrollDirection.down,
                [styles.scrollUp]: scrollDir === ScrollDirection.up,
            })}
        >
            <div className={clsx(styles.wrapper)}>
                <div
                    className={clsx(styles.navbar, {
                        [styles.hiddenNavbar]: scrollDir === ScrollDirection.down,
                        [styles.showNavbar]: scrollDir === ScrollDirection.up,
                    })}
                >
                    <Link
                        className={styles.logo}
                        href={`/${language}/`}
                    >
                        <div className={clsx(styles.logoWrap)}>
                            <NextImage
                                fill
                                src={translations.logo.filename}
                                overrideSrc={translations.logo.filename}
                                alt={translations.logo.alt}
                                priority
                            />
                        </div>
                    </Link>

                    {/* <SearchBar
                        isMenuOpen
                        translations={translations}
                    /> */}
                    <div
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={styles.menuDesktop}
                    >
                        {blok?.links?.map(link => (
                            <SubmenuDesktop
                                avoidAutoUnderline={hovered}
                                openMenus={menuIds}
                                toggleMenuVisibility={toggleMenuVisibility}
                                level={0}
                                blok={link}
                                key={link._uid}
                            />
                        ))}
                    </div>
                    <div className={styles.action}>
                        {alternates && (
                            <LanguageSwitcher
                                className={styles.onlyDesktop}
                                alternates={alternates}
                            />
                        )}
                        <Suspense fallback={<div style={{ width: '100%', height: 108 }}></div>}>
                            <CurrencySwitcher />
                        </Suspense>
                        {blok?.buttonLinks?.[0]?.title && <div className={styles.button}>{buttonComponent}</div>}
                        <div
                            onClick={() => setShowMobileMenu(prev => !prev)}
                            className={styles.menuMobile}
                        >
                            <Hamburger isActive={showMobileMenu} />
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={clsx(styles.showMobileMenu, {
                    [styles.isActive]: showMobileMenu,
                    bodyNoScroll: showMobileMenu,
                })}
            >
                <div className={styles.wrapperMobile}>
                    {blok?.links?.map(link => (
                        <SubmenuMobile
                            openMenus={menuIds}
                            toggleMenuVisibility={toggleMenuVisibility}
                            level={0}
                            blok={link}
                            key={link._uid}
                        />
                    ))}

                    <div className={styles.mobileAction}>
                        {blok?.buttonLinks?.[0]?.title && <div className={styles.button}>{buttonComponent}</div>}
                        {alternates && (
                            <LanguageSwitcher
                                className={styles.mobileSwitcher}
                                alternates={alternates}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
